import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { Calendar, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

export const BlogPage = () => {
  const { t, getLocalizedText, API } = useApp();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API}/blog`);
        const data = await res.json();
        setPosts(data.posts || []);
      } catch (err) {
        console.error('Failed to fetch blog posts', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [API]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8" data-testid="blog-page">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm tracking-[0.3em] uppercase text-gold mb-4">Rani Store</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1A1625]">
            {t('blog')}
          </h1>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[16/10] bg-gray-200 rounded-lg mb-4" />
                <div className="h-4 bg-gray-200 rounded mb-2 w-1/4" />
                <div className="h-6 bg-gray-200 rounded mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-4">No blog posts yet</p>
            <Link to="/" className="text-primary hover:underline">
              Return Home
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="group"
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden rounded-lg bg-gray-100 mb-4">
                    <img
                      src={post.featured_image || 'https://images.unsplash.com/photo-1763478959550-13d564c5a62d?q=80&w=800&auto=format&fit=crop'}
                      alt={getLocalizedText(post.title)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </Link>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(post.created_at)}</span>
                </div>
                <Link to={`/blog/${post.slug}`}>
                  <h2 className="font-heading text-xl font-semibold text-[#1A1625] group-hover:text-primary transition-colors mb-2">
                    {getLocalizedText(post.title)}
                  </h2>
                </Link>
                <p className="text-muted-foreground line-clamp-2 mb-4">
                  {getLocalizedText(post.excerpt) || getLocalizedText(post.content)?.substring(0, 150)}
                </p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center text-primary hover:text-gold transition-colors font-medium"
                >
                  {t('readMore')} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BlogPage;
